import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Post_Feedback } from '../../services/oprations/Feedback';
import Footer from '../../components/HeaderFooter/Footer';
import { useTheme } from '../../Context/themeContext';

const FeedbackPage = () => {
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 0,
    message: ''
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  // Theme-aware classes
  const isDark = theme === 'dark';
  
  const bgPrimary = isDark ? 'bg-[#0B1120]' : 'bg-gray-50';
  const bgCard = isDark ? 'bg-[#1E293B]' : 'bg-white';
  const bgInput = isDark ? 'bg-[#0F172A]' : 'bg-white';
  const bgSuccess = isDark ? 'bg-green-900/30' : 'bg-green-50';
  const bgError = isDark ? 'bg-red-900/30' : 'bg-red-50';
  
  const borderColor = isDark ? 'border-blue-900/30' : 'border-gray-200';
  const borderError = isDark ? 'border-red-700/50' : 'border-red-300';
  const borderFocus = isDark ? 'focus:border-blue-500 focus:ring-1 focus:ring-blue-500' : 'focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
  
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-600';
  const textBlue = isDark ? 'text-blue-400' : 'text-blue-600';
  //const textGreen = isDark ? 'text-green-400' : 'text-green-600';
  const textRed = isDark ? 'text-red-400' : 'text-red-600';
  const textMuted = isDark ? 'text-gray-500' : 'text-gray-400';
  
  //const shadowColor = isDark ? 'shadow-xl' : 'shadow-lg';
  const gradientButton = isDark 
    ? 'from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400'
    : 'from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500';
  
  const starColor = isDark ? 'text-gray-600' : 'text-gray-300';
  const starActive = 'text-yellow-400';

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (formData.rating === 0) {
      setError('Please select a rating');
      return;
    }
    if (!formData.message.trim()) {
      setError('Please enter your message');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await dispatch(Post_Feedback(formData));
      if (res?.success) {
        setSubmitSuccess(true);
        setTimeout(() => {
          setSubmitSuccess(false);
          setFormData({ name: '', email: '', rating: 0, message: '' });
          setHoverRating(0);
        }, 3000);
      } else {
        setError(res?.message || 'Failed to submit feedback. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const LoadingSpinner = () => (
    <div className={`min-h-screen ${bgPrimary} flex items-center justify-center transition-colors duration-300`}>
      <div className="text-center">
        <div className={`w-12 h-12 sm:w-16 sm:h-16 border-4 rounded-full animate-spin ${isDark ? 'border-blue-900/30 border-t-blue-400' : 'border-gray-200 border-t-blue-600'}`}></div>
        <p className={`mt-4 text-sm sm:text-base ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Loading...</p>
      </div>
    </div>
  );

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className={`min-h-screen ${bgPrimary} pt-20 pb-8 px-4 sm:px-6 md:px-8 transition-colors duration-300`}>
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        <div className="mb-6 text-center sm:mb-8 md:mb-12 animate-fadeIn">
          <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold ${textPrimary}`}>
            Share Your <span className={textBlue}>Feedback</span>
          </h1>
          <p className={`${textSecondary} text-sm sm:text-base mt-2`}>We value your opinion and strive to improve</p>
        </div>

        <div className="grid items-center grid-cols-1 gap-6 lg:grid-cols-2 md:gap-8">

          {/* Left Side - Single Overlapping Image with Background */}
          <div className="relative h-[400px] sm:h-[450px] md:h-[500px] w-full animate-slideInLeft hidden lg:block">
            {/* Background Pattern */}
            <div className="absolute inset-0">
              {/* Grid Pattern */}
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 20px 20px, ${isDark ? '#1e3a8a' : '#93c5fd'} 1px, transparent 1px)`,
                backgroundSize: '40px 40px',
                opacity: isDark ? 0.2 : 0.15
              }}></div>

              {/* Gradient Background */}
              <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-br from-blue-900/20 via-transparent to-blue-600/20' : 'bg-gradient-to-br from-blue-100/30 via-transparent to-blue-200/30'} rounded-3xl`}></div>

              {/* Decorative Circles */}
              <div className={`absolute top-20 left-20 w-32 h-32 sm:w-40 sm:h-40 ${isDark ? 'bg-blue-500/10' : 'bg-blue-400/10'} rounded-full blur-3xl`}></div>
              <div className={`absolute bottom-40 left-40 w-48 h-48 sm:w-60 sm:h-60 ${isDark ? 'bg-blue-400/10' : 'bg-blue-300/10'} rounded-full blur-3xl`}></div>
              <div className={`absolute top-60 left-10 w-24 h-24 sm:w-32 sm:h-32 ${isDark ? 'bg-blue-600/10' : 'bg-blue-400/10'} rounded-full blur-3xl`}></div>
            </div>

            {/* Main Image */}
            <div className="absolute top-[50px] left-[30px] sm:top-[60px] sm:left-[50px] md:top-[70px] md:left-[60px] w-[280px] sm:w-[380px] md:w-[450px] h-[320px] sm:h-[360px] md:h-[400px] rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-500 group">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Feedback"
                className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110"
              />
              {/* Gradient Overlay */}
              <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-blue-900/60 via-transparent to-transparent' : 'bg-gradient-to-t from-blue-800/40 via-transparent to-transparent'}`}></div>

              {/* Text on Image */}
              <div className="absolute text-white bottom-4 left-4">
                <p className="text-sm font-bold sm:text-lg">We Value</p>
                <p className="text-xl font-bold text-gray-400 dark:text-blue-300 sm:text-2xl">Your Opinion</p>
              </div>
            </div>

            {/* Stats Card - Floating */}
            <div className={`absolute bottom-[30px] right-[10px] sm:bottom-[40px] sm:right-[20px] md:bottom-[50px] md:right-[20px] ${bgCard} p-3 sm:p-4 rounded-xl border ${borderColor} shadow-xl animate-float transition-colors duration-300`}>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-lg sm:w-10 sm:h-10">
                  <svg className="w-4 h-4 text-white sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                </div>
                <div>
                  <div className={`text-xl sm:text-2xl font-bold ${textPrimary}`}>98%</div>
                  <div className={`text-[10px] sm:text-xs ${textBlue}`}>Satisfaction</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Feedback Form */}
          <div className={`${bgCard} p-5 sm:p-6 md:p-8 rounded-2xl border ${borderColor} animate-slideInRight transition-colors duration-300`}>
            <h2 className={`text-lg sm:text-xl md:text-2xl font-bold ${textPrimary} mb-4 md:mb-6`}>Send Feedback</h2>

            {submitSuccess ? (
              <div className={`${bgSuccess} p-6 rounded-lg text-center animate-scaleIn`}>
                <svg className="w-12 h-12 mx-auto mb-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className={`text-green-400 font-medium text-sm sm:text-base`}>Thank you for your feedback!</p>
                <p className={`${textSecondary} text-xs sm:text-sm mt-2`}>We appreciate your input</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                {/* Error Message */}
                {error && (
                  <div className={`${bgError} border ${borderError} rounded-lg p-3`}>
                    <p className={`${textRed} text-xs sm:text-sm`}>{error}</p>
                  </div>
                )}

                {/* Name Input */}
                <div>
                  <label className={`block ${textBlue} text-xs sm:text-sm mb-1`}>Your Name *</label>
                  <input
                    type="text"
                    placeholder="Enter your name..."
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className={`w-full px-4 py-3 sm:py-4 ${bgInput} border ${borderColor} rounded-lg ${textPrimary} text-sm focus:outline-none ${borderFocus} transition-all`}
                  />
                </div>

                {/* Email Input - Optional */}
                <div>
                  <label className={`block ${textBlue} text-xs sm:text-sm mb-1`}>Email (Optional)</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-4 py-3 sm:py-4 ${bgInput} border ${borderColor} rounded-lg ${textPrimary} text-sm focus:outline-none ${borderFocus} transition-all`}
                  />
                </div>

                {/* Rating */}
                <div>
                  <label className={`block ${textBlue} text-xs sm:text-sm mb-2`}>Rating *</label>
                  <div className="flex space-x-1 sm:space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="transition transform focus:outline-none hover:scale-110"
                        aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                      >
                        <svg className={`w-6 h-6 sm:w-7 sm:h-7 ${star <= (hoverRating || formData.rating) ? starActive : starColor}`} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </button>
                    ))}
                  </div>
                  {formData.rating > 0 && (
                    <p className="mt-1 text-xs text-green-400">
                      {formData.rating === 5 && "Excellent! ⭐"}
                      {formData.rating === 4 && "Very Good! 😊"}
                      {formData.rating === 3 && "Good 👍"}
                      {formData.rating === 2 && "Could be better 🤔"}
                      {formData.rating === 1 && "Needs improvement 😟"}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className={`block ${textBlue} text-xs sm:text-sm mb-1`}>Your Message *</label>
                  <textarea
                    placeholder="Tell us what you think..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows="4"
                    className={`w-full px-4 py-3 ${bgInput} border ${borderColor} rounded-lg ${textPrimary} text-sm focus:outline-none ${borderFocus} resize-none transition-all`}
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 sm:py-4 bg-gradient-to-r ${gradientButton} text-white rounded-lg text-sm sm:text-base font-semibold transition-all disabled:opacity-50 relative overflow-hidden group`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 mr-2 border-2 border-white rounded-full sm:w-5 sm:h-5 border-t-transparent animate-spin"></div>
                      Submitting...
                    </div>
                  ) : (
                    <span className="relative z-10">Submit Feedback</span>
                  )}
                </button>

                {/* Form Footer */}
                <p className={`${textMuted} text-xs text-center mt-4`}>
                  By submitting, you agree to our <span className={`${textBlue} hover:underline`}>Terms</span>
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Stats Bar for Mobile */}
        <div className="grid grid-cols-2 gap-3 mt-6 lg:hidden">
          <div className={`${bgCard} p-3 rounded-xl border ${borderColor} transition-colors duration-300`}>
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-lg">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
              </div>
              <div>
                <div className={`text-lg font-bold ${textPrimary}`}>98%</div>
                <div className={`text-[10px] ${textBlue}`}>Satisfaction Rate</div>
              </div>
            </div>
          </div>
          <div className={`${bgCard} p-3 rounded-xl border ${borderColor} transition-colors duration-300`}>
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-purple-600 rounded-lg">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <div className={`text-lg font-bold ${textPrimary}`}>500+</div>
                <div className={`text-[10px] ${textBlue}`}>Happy Clients</div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn { 
          animation: fadeIn 0.8s ease-out forwards; 
        }
        .animate-slideInLeft { 
          animation: slideInLeft 0.8s ease-out forwards; 
        }
        .animate-slideInRight { 
          animation: slideInRight 0.8s ease-out forwards; 
        }
        .animate-float { 
          animation: float 3s ease-in-out infinite; 
        }
        .animate-scaleIn { 
          animation: scaleIn 0.5s ease-out forwards; 
        }
        
        @media (max-width: 640px) {
          .animate-slideInLeft, .animate-slideInRight {
            animation-duration: 0.5s;
          }
        }
      `}</style>
    </div>
  );
};

export default FeedbackPage;
